import React, { useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { MobXProviderContext, observer } from 'mobx-react';
import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import NavigationService from '../services/NavigationService';
import VerticalCourseTOCService from '../services/VerticalCourseTOCService';

import '../css/VerticalCourseTOC.less';

const VerticalCourseTOC = observer((props) => {
  const { history, t } = props;
  const {
    classroomManager, courseManager, verticalCourseTOCManager
  } = useContext(MobXProviderContext);

  const VerticalCourseTOCHeader = useMemo(() => SatCoreComponent('VerticalCourseTOCHeader'), []);
  const VerticalCourseTOCRow = useMemo(() => SatCoreComponent('VerticalCourseTOCRow'), []);

  const panelRef = useRef(null);

  const { currentClassroomId } = classroomManager;
  const { currentCourseId, currentElementId, fullCourseResource, isTreeLoading } = courseManager;
  const {
    allowVerticalCourseTOC, isLoadingVerticalCourseTOC, isVerticalCourseTOCOpen, verticalCourseTOCExpandedIds
  } = verticalCourseTOCManager;

  const verticalCourseTOCTreeLookup = useMemo(() => {
    return VerticalCourseTOCService.buildVerticalCourseTOCTreeLookup(fullCourseResource);
  }, [fullCourseResource]);

  // Stable string form of expanded IDs; used as a dep so the save-on-change
  // effect fires when the set contents change (Set identity alone does not).
  const expandedIdsString = Array.from(verticalCourseTOCExpandedIds).sort().join(',');

  // Refresh the vertical TOC state on mount and whenever classroom or course
  // changes.
  //
  // If sessionStorage holds a location matching the current courseId,
  // restore its expanded IDs into the manager; otherwise clear the TOC state
  // on the manager (e.g., expanded IDs, panel open state, scroll position).
  //
  // If VerticalCourseTOC was entered via a course-card click and we have a
  // saved element, update the router path to that element before the tree
  // fetch — CourseElementList (the cards adjacent to the TOC) lands directly
  // on the saved element without a brief flash of root cards.
  //
  // Then fetch the tree. If there was no saved state for this course, auto-
  // select the first available TOC row so the user lands on real content
  // instead of an empty root.
  //
  // Single-slot per classroom, mirroring the classic
  // c2c_lastCourseLocation_* pattern (a courseId mismatch indicates we entered a
  // different course).
  useEffect(() => {
    if (!currentClassroomId || !currentCourseId) {
      return;
    }
    const refreshVerticalCourseTOCState = async () => {
      verticalCourseTOCManager.setIsLoadingVerticalCourseTOC(true);
      courseManager.setTreeLoading(true);
      try {
        courseManager.setFullCourseResource(null);

        // Prefer the URL's courseId over courseManager.currentCourseId — URL is the
        // authoritative source for the course we are actually navigating to.
        const routerUrlCourseId = new URLSearchParams(history.location.search).get('courseId') || currentCourseId;

        const savedLocation = allowVerticalCourseTOC
          ? VerticalCourseTOCService.getLastVerticalCourseTOCLocation(currentClassroomId)
          : null;
        const hasSavedLocationForCurrentCourse = savedLocation?.courseId === routerUrlCourseId
          && Array.isArray(savedLocation.expandedCourseElementIds);

        if (hasSavedLocationForCurrentCourse) {
          verticalCourseTOCManager.setVerticalCourseTOCExpandedIds(savedLocation.expandedCourseElementIds);

          // Set currentElementId prior to calling getFullCourseResource()
          // This prevents briefly marking the root (course) as 'selected' when it should not be.
          const isFromCourseCardClick = history.location.state?.isFromCourseCardClick === true;
          if (isFromCourseCardClick && savedLocation.courseElementId) {
            courseManager.setCurrentElementId(savedLocation.courseElementId);
          }
        } else {
          verticalCourseTOCManager.clearAll();
        }

        await courseManager.getFullCourseResource(currentClassroomId, currentCourseId);

        if (!allowVerticalCourseTOC || !courseManager.fullCourseResource) {
          return;
        }

        // If we entered via a course-card click and have a saved element,
        // restore selection via the same nav flow used by row clicks.
        // Initial entry (no saved state) leaves the course header as the default
        // selection — no auto-drill into the first row.
        const isFromCourseCardClick = history.location.state?.isFromCourseCardClick === true;
        if (hasSavedLocationForCurrentCourse && isFromCourseCardClick && savedLocation.courseElementId) {
          const treeLookup = VerticalCourseTOCService.buildVerticalCourseTOCTreeLookup(courseManager.fullCourseResource);
          const targetCourseTOCElement = treeLookup.elementByIdMap.get(savedLocation.courseElementId);
          if (targetCourseTOCElement) {
            await NavigationService.handleCourseElementClick(targetCourseTOCElement, history);
          }
        }
      } finally {
        courseManager.setTreeLoading(false);
        verticalCourseTOCManager.setIsLoadingVerticalCourseTOC(false);
      }
    };
    refreshVerticalCourseTOCState();
  }, [currentClassroomId, currentCourseId]);

  // Auto-open ancestors of the current selection and scroll it into view
  // whenever the tree loads or the selection changes.
  useEffect(() => {
    if (!fullCourseResource || !currentElementId) {
      return;
    }
    const ancestorPath = verticalCourseTOCTreeLookup.ancestorPathByIdMap.get(currentElementId);
    if (Array.isArray(ancestorPath) && ancestorPath.length > 0) {
      const merged = new Set(verticalCourseTOCManager.verticalCourseTOCExpandedIds);
      let added = false;
      ancestorPath.forEach((id) => {
        if (!merged.has(id)) {
          merged.add(id);
          added = true;
        }
      });
      if (added) {
        verticalCourseTOCManager.setVerticalCourseTOCExpandedIds(Array.from(merged));
      }
    }
    // Auto-scroll to the selected VerticalCourseTOCRow.
    // Query by data-course-toc-element-id (not `.selected` class) so we look up the target directly
    // by the id we know from currentElementId - avoids DOM-lag on the `.selected` class update.
    // Target the row-content, not the outer <li>, since the <li> contains the whole expanded subtree.
    requestAnimationFrame(() => {
      const selectedRowContent = panelRef.current?.querySelector(
        `[data-course-toc-element-id="${currentElementId}"] > .vertical-course-toc-row-content`
      );
      if (selectedRowContent) {
        selectedRowContent.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [fullCourseResource, currentElementId]);

  // Save the vertical TOC location to sessionStorage whenever the expanded
  // set OR the current selection changes.
  useEffect(() => {
    if (!allowVerticalCourseTOC) {
      return;
    }
    if (!currentClassroomId || !currentCourseId || !fullCourseResource) {
      return;
    }
    VerticalCourseTOCService.saveLastVerticalCourseTOCLocation(
      currentClassroomId,
      currentCourseId,
      currentElementId,
      verticalCourseTOCManager.verticalCourseTOCExpandedIds
    );
  }, [expandedIdsString, currentElementId, currentClassroomId, currentCourseId, fullCourseResource]);

  const { CourseResourceElements: rootCourseTOCElements = [] } = fullCourseResource || {};

  const handleTabClick = () => {
    verticalCourseTOCManager.setIsVerticalCourseTOCOpen(!verticalCourseTOCManager.isVerticalCourseTOCOpen);
  };

  const rootClassName = classNames('VerticalCourseTOC', {
    'closed': !isVerticalCourseTOCOpen,
    'is-loading': isTreeLoading || isLoadingVerticalCourseTOC,
    'open': isVerticalCourseTOCOpen
  });

  return (
    <div className={rootClassName}>
      <div
        ref={panelRef}
        aria-hidden={!isVerticalCourseTOCOpen}
        className='vertical-course-toc-panel'
        id='vertical-course-toc-panel'>
        <VerticalCourseTOCHeader history={history} />
        <ul
          aria-label={t('treeAriaLabel')}
          className='vertical-course-toc-tree'
          role='tree'>
          {rootCourseTOCElements.map((rootCourseTOCElement, index) => {
            return (
              <VerticalCourseTOCRow
                key={rootCourseTOCElement.id}
                ariaLevel={1}
                ariaPosInSet={index + 1}
                ariaSetSize={rootCourseTOCElements.length}
                courseTOCElement={rootCourseTOCElement}
                history={history} />
            );
          })}
        </ul>
      </div>
      <button
        aria-controls='vertical-course-toc-panel'
        aria-expanded={isVerticalCourseTOCOpen}
        aria-label={isVerticalCourseTOCOpen ? t('closeButtonAriaLabel') : t('openButtonAriaLabel')}
        className='vertical-course-toc-tab'
        onClick={handleTabClick}
        type='button'>
        <span className='vertical-course-toc-tab-label'>{t('content')}</span>
      </button>
    </div>
  );
});

export default VerticalCourseTOC;
SatCoreRegister('VerticalCourseTOC', VerticalCourseTOC);
